import { useEffect, useRef } from 'react';

const DEBOUNCE_DELAY_MS = 500;

/**
 * Hook to debounce subscription updates
 * The handler function is recreated on each render with latest changes,
 * and the timeout is reset whenever changes occur, ensuring latest values are used
 */
export const useDebouncedSubscription = <T,>(
  isFeedOpen: boolean,
  hasInitialized: boolean,
  changes: { toAdd: T[]; toRemove: T[] },
  handler: () => void,
  changesKey?: string // Optional stable key to prevent unnecessary resets
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handlerRef = useRef(handler);
  const prevChangesKeyRef = useRef<string>('');
  const capturedChangesRef = useRef<{ toAdd: T[]; toRemove: T[] } | null>(null);
  
  // Keep handler ref up to date - handler is recreated with latest changes on each render
  handlerRef.current = handler;

  useEffect(() => {
    if (!isFeedOpen || !hasInitialized) {
      // Clear debounce timeout if feed closes or not initialized
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      capturedChangesRef.current = null;
      return;
    }

    // Use stable key if provided, otherwise create one from changes
    const createKey = (toAdd: T[], toRemove: T[]) => {
      const toAddKey = toAdd.length > 0 ? `toAdd:${toAdd.sort().join(',')}` : '';
      const toRemoveKey = toRemove.length > 0 ? `toRemove:${toRemove.sort().join(',')}` : '';
      const parts = [toAddKey, toRemoveKey].filter(Boolean);
      return parts.length > 0 ? parts.join('|') : '';
    };
    const currentChangesKey = changesKey || createKey(changes.toAdd || [], changes.toRemove || []);
    
    // If changesKey becomes empty but we have a pending timeout with captured changes,
    // don't clear it - let it fire with the captured changes
    if (!currentChangesKey || currentChangesKey === '') {
      // Only clear timeout if we don't have captured changes (nothing pending)
      if (timeoutRef.current && !capturedChangesRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Don't update prevChangesKeyRef if we have captured changes (let timeout fire)
      if (!capturedChangesRef.current) {
        prevChangesKeyRef.current = currentChangesKey;
      }
      return;
    }
    
    // Only reset debounce if changes actually changed (by content, not reference)
    if (currentChangesKey === prevChangesKeyRef.current && timeoutRef.current) {
      return;
    }
    
    // Capture the changes at this moment so we use them even if changes object updates later
    capturedChangesRef.current = {
      toAdd: [...(changes.toAdd || [])],
      toRemove: [...(changes.toRemove || [])],
    };

    // Clear existing timeout when changes occur (resets debounce timer)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    prevChangesKeyRef.current = currentChangesKey;

    // Debounce subscription updates
    // Use captured changes to ensure we subscribe even if changes object updates before timeout fires
    timeoutRef.current = setTimeout(() => {
      const captured = capturedChangesRef.current;
      // Only call handler if we still have captured changes (they weren't cleared)
      if (captured) {
        handlerRef.current();
      }
      timeoutRef.current = null;
      capturedChangesRef.current = null;
    }, DEBOUNCE_DELAY_MS);

    // Cleanup timeout on unmount or dependency change
    // But don't clear if we have captured changes - let the timeout fire
    return () => {
      // Only clear timeout if we don't have captured changes pending
      // This prevents clearing a timeout that's about to fire with valid changes
      if (timeoutRef.current && !capturedChangesRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isFeedOpen, hasInitialized, changesKey]); // Use changesKey instead of changes to prevent recalculation issues
};

